import { Button, Spinner } from 'flowbite-react';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { screenRejectCandidate } from 'src/apis/application';
import { useToast } from 'src/context/ToastProvider';
import { ScreenRejectCandidateInput } from 'types';

const refetchPositionRecommendations = async (queryClient: ReturnType<typeof useQueryClient>) => {
	await queryClient.invalidateQueries({ queryKey: ['getRecommendationsForPosition'], exact: false });
	await new Promise(resolve => setTimeout(resolve, 100));
	await queryClient.refetchQueries({ queryKey: ['getRecommendationsForPosition'], exact: false });
};

const RecruiterCandidateScreenReject = ({
	userName,
	userId,
	positionId,
	isLoading: externalIsLoading,
	onFinish,
	onSubmit,
}: {
	userName: string;
	userId?: string;
	positionId?: string;
	isLoading?: boolean;
	onFinish?: () => void;
	onSubmit?: (data: ScreenRejectCandidateInput) => void;
}) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const [searchParams, setSearchParams] = useSearchParams();

	const screenRejectFn = useMutation(['screenRejectCandidate'], screenRejectCandidate, {
		onSuccess: async () => {
			toast({
				title: `Screen rejected ${userName}`,
				type: 'success',
			});
			await refetchPositionRecommendations(queryClient);
			searchParams.set('tab', 'rejected');
			setSearchParams(searchParams);
			onFinish && onFinish();
		},
		onError(error: Error) {
			toast({
				title: `Error while screen rejecting candidate: ${error.message}`,
				type: 'error',
			});
		},
	});

	return (
		<div className="flex flex-col gap-4">
			<p className="text-sm text-[#585757]">
				Are you sure you want to screen reject <strong>{userName}</strong>? The candidate will be moved to the Rejected tab. Email and Slack
				notifications will be sent.
			</p>
			<Button
				color="failure"
				className="w-fit"
				onClick={() => {
					if (onSubmit) {
						onSubmit({ userId: userId ?? '', positionId: positionId ?? '' });
					} else {
						screenRejectFn.mutate({ userId: userId ?? '', positionId: positionId ?? '' });
					}
				}}
				disabled={screenRejectFn.isLoading || externalIsLoading}
			>
				{screenRejectFn.isLoading || externalIsLoading ? <Spinner /> : 'Confirm Screen Reject'}
			</Button>
		</div>
	);
};

export default RecruiterCandidateScreenReject;
